import { Suspense } from 'react';

import { getAccountHasPendingPayout } from '@helpers/Accounts/getAccountHasPendingPayout';
import { FormattedCurrency } from '@iwIntlProvider/FormattedCurrency';
import { logError, TAGS } from '@iwLogger/IwDataDogLogger';
import { fetchGetAccounts } from '@iwoca/lapi-client/edge';
import { AlertBox, Text } from '@iwoca/orion';
import { HourglassSVG } from '@svgComponents/HourglassSVG';
import { useQuery } from '@tanstack/react-query';
import { Skeleton } from '@ui';
import { FormattedMessage } from 'react-intl';

import { ErrorBoundary } from '../../../../errorBoundary/ErrorBoundary';
import { useStateKey } from '../../../../iwQueryClient/useStateKey';
import FallbackErrorModal from '../../../modals/fallbackErrorModal/FallbackErrorModal';

export const PendingPayoutBanner = ({ accountId }: { accountId: string }) => {
    return (
        <ErrorBoundary
            fallback={(error) => <FallbackErrorModal error={error} />}
        >
            <Suspense
                fallback={
                    <Skeleton
                        data-testid="pending-payout-banner-skeleton"
                        className="h-[60px] w-full rounded-s"
                    />
                }
            >
                <PendingPayout accountId={accountId} />
            </Suspense>
        </ErrorBoundary>
    );
};

const PendingPayout = ({ accountId }: { accountId: string }) => {
    const stateKey = useStateKey();
    const { data: account } = useQuery({
        queryKey: ['accounts', stateKey],
        queryFn: () => fetchGetAccounts({ stateKey }),
        select: (data) =>
            data.data.accounts.find(
                (account) => account.account_id === accountId
            ),
        suspense: true
    });

    if (!account) {
        const errorMessage = `PendingPayoutBanner - Account with account_id: ${accountId} not found`;

        logError(errorMessage, { tags: TAGS.DE_LENDING });
        throw new Error(errorMessage);
    }

    const { hasPendingPayout, pendingPayoutAmount } =
        getAccountHasPendingPayout(account);

    if (!hasPendingPayout) return null;

    return (
        <AlertBox
            variant="connect"
            className="flex flex-row items-center gap-m"
            data-testid="pending-payout-banner"
        >
            <HourglassSVG />
            <div>
                <Text
                    data-testid="pending-payout-title"
                    className="text-s font-bold"
                >
                    <FormattedMessage
                        id="_.outstanding_view.pending_payout_banner_title"
                        defaultMessage="You will receive {payoutAmount} shortly"
                        values={{
                            payoutAmount: (
                                <FormattedCurrency
                                    value={pendingPayoutAmount}
                                    decimalPlaces={0}
                                />
                            )
                        }}
                    />
                </Text>
                <Text className="text-s">
                    <FormattedMessage
                        id="_.outstanding_view.pending_payout_banner_description"
                        defaultMessage="It usually takes a few minutes for your account to update."
                    />
                </Text>
            </div>
        </AlertBox>
    );
};
